import { Currency, Ether, NativeCurrency, Token } from '@uniswap/sdk-core'
import invariant from 'tiny-invariant'

import { UNI_ADDRESS } from './addresses'
import { SupportedChainId } from './chains'

export const NATIVE_CHAIN_ID = 'NATIVE'

// When decimals are not specified for an ERC20 token
// use default ERC20 token decimals as specified here:
// https://docs.openzeppelin.com/contracts/3.x/erc20
export const DEFAULT_ERC20_DECIMALS = 18

export const WNATIVE_MAINNET = new Token(
  SupportedChainId.PLANQ_MAINNET,
  '0x5EBCdf1De1781e8B5D41c016B0574aD53E2F6E1A',
  18,
  'WPLQ',
  'Wrapped Planq'
)

const WNATIVE_TESTNET = new Token(
  SupportedChainId.PLANQ_TESTNET,
  '0xa9898AaFAbAeC0Bb762F96b7cC1e284F943c0114',
  18,
  'WPLQ',
  'Wrapped Planq'
)

export const USDC_MAINNET = new Token(
  SupportedChainId.PLANQ_MAINNET,
  '0x75E20C5d4aade76143b8b74d1C5E2865347f9d3B',
  18,
  'USDC',
  'USD//C'
)

export const USDT_MAINNET = new Token(
  SupportedChainId.PLANQ_MAINNET,
  '0xfD6fF17b542260f95660BBD71470Fe6eEC72801D',
  18,
  'USDT',
  'Tether USD'
)

export const WBNB_MAINNET = new Token(
  SupportedChainId.PLANQ_MAINNET,
  '0xeAcB5c09612F90eb2EE3bA9896d8421084FFb921',
  18,
  'WBNB',
  'Wrapped BNB'
)

export const UNI: { [chainId: number]: Token } = {
  [SupportedChainId.PLANQ_MAINNET]: new Token(
    SupportedChainId.PLANQ_MAINNET,
    UNI_ADDRESS[SupportedChainId.PLANQ_MAINNET],
    18,
    'QSWAP',
    'Qswap'
  ),
  [SupportedChainId.PLANQ_TESTNET]: new Token(
    SupportedChainId.PLANQ_TESTNET,
    UNI_ADDRESS[SupportedChainId.PLANQ_TESTNET],
    18,
    'QSWAP',
    'Qswap'
  ),
}

export const WRAPPED_NATIVE_CURRENCY: { [chainId: number]: Token | undefined } = {
  [SupportedChainId.PLANQ_MAINNET]: WNATIVE_MAINNET,
  [SupportedChainId.PLANQ_TESTNET]: WNATIVE_TESTNET,
}

function isPlanq(chainId: number): chainId is SupportedChainId.PLANQ_MAINNET | SupportedChainId.PLANQ_TESTNET {
  return chainId === SupportedChainId.PLANQ_MAINNET || chainId === SupportedChainId.PLANQ_TESTNET
}

class PlanqNativeCurrency extends NativeCurrency {
  equals(other: Currency): boolean {
    return other.isNative && other.chainId === this.chainId
  }

  get wrapped(): Token {
    if (!isPlanq(this.chainId)) throw new Error('Not planq')
    const wrapped = WRAPPED_NATIVE_CURRENCY[this.chainId]
    invariant(wrapped instanceof Token)
    return wrapped
  }

  public constructor(chainId: number) {
    if (!isPlanq(chainId)) throw new Error('Not planq')
    super(chainId, 18, 'PLQ', 'Planq')
  }
}

class ExtendedEther extends Ether {
  public get wrapped(): Token {
    const wrapped = WRAPPED_NATIVE_CURRENCY[this.chainId]
    if (wrapped) return wrapped
    throw new Error('Unsupported chain ID')
  }

  private static _cachedExtendedEther: { [chainId: number]: NativeCurrency } = {}

  public static onChain(chainId: number): ExtendedEther {
    return this._cachedExtendedEther[chainId] ?? (this._cachedExtendedEther[chainId] = new ExtendedEther(chainId))
  }
}

const cachedNativeCurrency: { [chainId: number]: NativeCurrency | Token } = {}
export function nativeOnChain(chainId: number): NativeCurrency | Token {
  if (cachedNativeCurrency[chainId]) return cachedNativeCurrency[chainId]
  let nativeCurrency: NativeCurrency | Token
  if (isPlanq(chainId)) {
    nativeCurrency = new PlanqNativeCurrency(chainId)
  } else {
    nativeCurrency = ExtendedEther.onChain(chainId)
  }
  return (cachedNativeCurrency[chainId] = nativeCurrency)
}

export const TOKEN_SHORTHANDS: { [shorthand: string]: { [chainId in SupportedChainId]?: string } } = {}
